import { Calendar, Check, CloudOff, RotateCw, TriangleAlert, Users } from "lucide-react";
import { EmptyIllustration } from "./kit/EmptyIllustration";
import { COMPLETION_PROMPT, boardStatus, hasReflectionText, isPlanned, needsReflection } from "../lib/trainingStatus";
import { coachPlayersName } from "../lib/coachPlayersName";
import { formatClockTime12h } from "../lib/clockTime";

/**
 * Issue #1281 (TrainingHome board, round 10, D243; D259; D272): the My
 * training session card and its three list states, for a player on the new
 * look only. `TrainingSessions.jsx` picks these when `newLook && !isCoach`;
 * the old look and a coach keep `SessionCard` and the old empty copy (D217).
 * Values are the board's, in `--nw-*` tokens.
 */

const CARD = { background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)" };
const CHIP = { padding: "2px 8px", fontSize: 11, fontWeight: 600, borderRadius: "var(--nw-radius-pill)", whiteSpace: "nowrap" };
const SENTENCE = { fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink)" };
const ROW_BUTTON = {
  display: "flex", flexDirection: "column", gap: 6, width: "100%", minHeight: 44, textAlign: "left",
  background: "none", border: 0, padding: 0, margin: 0, font: "inherit", color: "inherit", cursor: "pointer",
};
const SMALL_CONTROL = {
  display: "inline-flex", alignItems: "center", gap: 6, alignSelf: "flex-start", minHeight: 44, padding: "0 14px",
  background: "var(--nw-surface)", border: "1px solid var(--nw-line-strong)", borderRadius: "var(--nw-radius-control)",
  fontSize: 14, fontWeight: 600, color: "var(--nw-navy)", boxShadow: "none",
};

/** `<date> · <h:mm am/pm> · <ground>`, any part dropped when empty. */
export function whenLine(s) {
  return [s.training_date, formatClockTime12h(s.training_time), s.location].filter(Boolean).join(" · ");
}

export function BoardSessionCard({ s, onOpen, onAddReflection }) {
  const status = boardStatus(s);
  const owed = status?.testid === "needs-reflection";
  const when = whenLine(s);
  return (
    <div
      data-testid={`ts-card-${s.session_id}`}
      style={{
        ...CARD, display: "flex", flexDirection: "column", gap: 6, padding: "12px 14px",
        border: owed ? "1px solid var(--nw-navy)" : CARD.border,
      }}
    >
      <button type="button" data-testid={`ts-open-${s.session_id}`} onClick={onOpen} style={ROW_BUTTON}>
        <span style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {status && (
            <span
              data-testid={`ts-status-${status.testid}-${s.session_id}`}
              style={{
                ...CHIP,
                background: status.emphasis ? "var(--nw-navy)" : "var(--nw-line-faint)",
                color: status.emphasis ? "var(--nw-surface)" : "var(--nw-ink-lede)",
              }}
            >
              {status.label}
            </span>
          )}
          <span style={{ ...CHIP, background: "var(--nw-surface)", color: "var(--nw-ink-lede)", border: "1px solid var(--nw-line)" }}>
            {s.category}
          </span>
        </span>
        <span className="nw-rowname">{s.session_title}</span>
        {when && <span className="nw-help" data-testid={`ts-when-${s.session_id}`}>{when}</span>}
        {owed && <span style={SENTENCE}>{COMPLETION_PROMPT}</span>}
        {status?.testid === "reflected" && s.lesson_learned && (
          <span style={SENTENCE}>{`Lesson learned: ${s.lesson_learned}`}</span>
        )}
      </button>
      {owed && (
        <button type="button" data-testid={`ts-add-reflection-${s.session_id}`} onClick={onAddReflection} style={SMALL_CONTROL}>
          Add how it went
        </button>
      )}
    </div>
  );
}

export function BoardLoading() {
  return (
    <div data-testid="ts-loading" aria-busy="true" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {[0, 1, 2].map((i) => (
        <div key={i} style={{ ...CARD, display: "flex", flexDirection: "column", gap: 8, padding: "12px 14px", height: 90, boxSizing: "border-box" }}>
          <div style={{ width: "50%", height: 12, borderRadius: 6, background: "var(--nw-line)" }} />
          <div style={{ width: "30%", height: 10, borderRadius: 5, background: "var(--nw-line-faint)" }} />
        </div>
      ))}
    </div>
  );
}

export function BoardEmpty() {
  return (
    <div data-testid="ts-empty" style={{ ...CARD, display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "28px 20px", textAlign: "center" }}>
      <EmptyIllustration name="training" />
      <div style={{ fontWeight: 600, fontSize: 15, color: "var(--nw-ink)" }}>No training sessions captured yet.</div>
      <div className="nw-lede">Tap Capture session to log your first one.</div>
    </div>
  );
}

/** D280: a filter that matches nothing is not "none captured". */
export function BoardFilteredEmpty() {
  return (
    <div data-testid="ts-filtered-empty" className="nw-help" style={{ ...CARD, padding: "16px 14px" }}>
      Nothing matches this filter. Clear it to see all your sessions.
    </div>
  );
}

export function BoardLoadFailed({ onRetry }) {
  return (
    <div data-testid="ts-load-error" style={{ ...CARD, display: "flex", flexDirection: "column", gap: 8, padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <CloudOff size={20} aria-hidden="true" style={{ color: "var(--nw-alert)", flexShrink: 0 }} />
        <div className="nw-body-strong">Couldn't load your training sessions</div>
      </div>
      <div style={{ fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink-muted)" }}>
        Nothing has been lost — we just couldn't reach the server.
      </div>
      <button type="button" data-testid="ts-load-error-retry" onClick={onRetry} style={SMALL_CONTROL}>
        <RotateCw size={14} aria-hidden="true" />
        Try again
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The coach's Sessions section (#1159, board CoachTrainingSessions, round 12,
// D298). A coach reads one player's sessions at a time; the card carries the
// words a coach needs (who it is for, whether the player has reflected) and no
// edit, delete or "Add how it went": a coach can't (`trainingSessions.ts`).
// ---------------------------------------------------------------------------

/**
 * The coach card's status chip: Planned (navy tint), Waiting on <player> (a
 * planned session whose date has passed) or Reflected (the player wrote text).
 * A completed session with no text gets no status chip (D279).
 */
export function coachBoardStatus(s, playerName) {
  if (needsReflection(s)) return { label: `Waiting on ${playerName}`, testid: "waiting", waiting: true };
  if (isPlanned(s)) return { label: "Planned", testid: "planned", planned: true };
  if (hasReflectionText(s)) return { label: "Reflected", testid: "reflected" };
  return null;
}

/**
 * S1 (waiting on Andreas): the board makes the card a "session-open" row, but
 * no board draws what it opens, so the card is a plain `div` with no button and
 * no chevron until that is ruled.
 */
export function CoachSessionCard({ s, playerName }) {
  const status = coachBoardStatus(s, playerName);
  const when = whenLine(s);
  const forLine = `For ${playerName}${s.skill_topic ? ` · focus: ${s.skill_topic}` : ""}`;
  return (
    <div data-testid={`ts-card-${s.session_id}`} style={{ ...CARD, display: "flex", flexDirection: "column", gap: 6, padding: "12px 14px" }}>
      <span style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {status && (
          <span
            data-testid={`ts-status-${status.testid}-${s.session_id}`}
            style={{
              ...CHIP,
              background: status.planned ? "var(--nw-navy-tint)" : "var(--nw-line-faint)",
              color: status.planned ? "var(--nw-navy)" : "var(--nw-ink-lede)",
            }}
          >
            {status.label}
          </span>
        )}
        <span style={{ ...CHIP, background: "var(--nw-surface)", color: "var(--nw-ink-lede)", border: "1px solid var(--nw-line)" }}>
          {s.category}
        </span>
      </span>
      <span className="nw-rowname">{s.session_title}</span>
      {when && <span className="nw-help" data-testid={`ts-when-${s.session_id}`}>{when}</span>}
      <span className="nw-help" data-testid={`ts-for-${s.session_id}`}>{forLine}</span>
      {status?.waiting && (
        <span style={SENTENCE} data-testid={`ts-waiting-${s.session_id}`}>{`The session has been. ${playerName} hasn't added how it went yet.`}</span>
      )}
      {status?.testid === "reflected" && s.lesson_learned && (
        <span style={SENTENCE}>{`Lesson learned: ${s.lesson_learned}`}</span>
      )}
    </div>
  );
}

/** The sessions read, or the roster read, failed: never "no sessions". */
export function CoachLoadFailed({ onRetry }) {
  return (
    <div data-testid="ts-coach-load-error" style={{ ...CARD, display: "flex", flexDirection: "column", gap: 8, padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <CloudOff size={20} aria-hidden="true" style={{ color: "var(--nw-alert)", flexShrink: 0 }} />
        <div className="nw-body-strong">Couldn't load the sessions</div>
      </div>
      <div style={{ fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink-muted)" }}>
        Nothing has been lost — we just couldn't reach the server.
      </div>
      <button type="button" data-testid="ts-coach-load-error-retry" onClick={onRetry} style={SMALL_CONTROL}>
        <RotateCw size={14} aria-hidden="true" />
        Try again
      </button>
    </div>
  );
}

function CoachStateCard({ testId, title, icon: Icon, children }) {
  return (
    <div data-testid={testId} style={{ ...CARD, display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "28px 20px", textAlign: "center" }}>
      <div aria-hidden="true" style={{ width: 52, height: 52, borderRadius: 16, background: "var(--nw-navy-tint)", color: "var(--nw-navy)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Icon size={22} aria-hidden="true" style={{ color: "var(--nw-navy)" }} />
      </div>
      <div style={{ fontWeight: 600, fontSize: 15, color: "var(--nw-ink)" }}>{title}</div>
      <div className="nw-lede">{children}</div>
    </div>
  );
}

export function CoachNoPlayers() {
  return (
    <CoachStateCard testId="ts-coach-no-players" icon={Users} title="You have no linked players yet.">
      {`Sessions are scheduled for a player. Invite one from ${coachPlayersName()}.`}
    </CoachStateCard>
  );
}

/**
 * S2 (ruled by D335): the board's line says
 * "…and it shows here, soonest first", but the server returns newest first and
 * D278 keeps the server's order, so the clause is left out.
 */
export function CoachEmpty({ playerName }) {
  return (
    <CoachStateCard testId="ts-coach-empty" icon={Calendar} title={`No sessions scheduled for ${playerName} yet.`}>
      Schedule a session and it shows here.
    </CoachStateCard>
  );
}

/**
 * The line that stays after a session is scheduled (D48): the board's white row
 * with a 3px navy left border, a navy check and 14px/600 ink (`ts-scheduled`).
 */
export function CoachScheduledLine({ children }) {
  return (
    <div
      role="status"
      data-testid="ts-coach-scheduled"
      style={{ ...CARD, borderRadius: 10, borderLeftWidth: 3, borderLeftColor: "var(--nw-navy)", display: "flex", alignItems: "center", gap: 10, padding: "12px 14px" }}
    >
      <Check size={16} strokeWidth={2.5} aria-hidden="true" style={{ color: "var(--nw-navy)", flexShrink: 0 }} />
      <span style={{ fontSize: 14, fontWeight: 600, color: "var(--nw-ink)" }}>{children}</span>
    </div>
  );
}

/**
 * The failed save, in the sheet above Save (D57; the board's `ts-save-failed`):
 * a 1px line border with a 3px alert-red left border, a warning glyph, a
 * 14px/600 title and a 12px muted body.
 */
export function CoachSaveFailed() {
  return (
    <div
      role="alert"
      data-testid="ts-coach-save-error"
      style={{
        display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px", background: "var(--nw-surface)", border: "1px solid var(--nw-line)",
        borderLeftWidth: 3, borderLeftColor: "var(--nw-alert)", borderRadius: 10,
      }}
    >
      <TriangleAlert size={16} aria-hidden="true" style={{ color: "var(--nw-alert)", flexShrink: 0, marginTop: 2 }} />
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--nw-ink)" }}>Failed to save</div>
        <div style={{ fontSize: 12, lineHeight: 1.5, color: "var(--nw-ink-muted)" }}>Everything you typed is still in the sheet. Try again.</div>
      </div>
    </div>
  );
}

/**
 * The coach's session list for the state it is in (extracted from
 * `TrainingSessions` to keep its complexity down): failed, loading, no players,
 * empty, filtered-empty or the cards. A roster still on its way, or a player
 * whose read has not come back, is loading and never "empty" (D53 ruling 2).
 */
export function CoachSessionsList({ rosterState, loadState, loadedFor, roster, items, playerId, playerName, filtered, onRetry }) {
  if (rosterState === "failed" || (rosterState === "ready" && loadState === "failed")) return <CoachLoadFailed onRetry={onRetry} />;
  if (rosterState === "loading" || (roster.length > 0 && (loadState === "loading" || loadedFor !== playerId))) return <BoardLoading />;
  if (roster.length === 0) return <CoachNoPlayers />;
  if (items.length === 0) return filtered ? <BoardFilteredEmpty /> : <CoachEmpty playerName={playerName} />;
  return (
    <div data-testid="ts-list" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {items.map((s) => <CoachSessionCard key={s.session_id} s={s} playerName={playerName} />)}
    </div>
  );
}
